import { isValid } from "date-fns";
import {
  CompetenceActivityClass,
  EvidenceDefinition,
  EvidencePart,
  IEvidence,
  INOSSelectCompetence,
  INOSSelectLearningObjectives,
  ProgramData,
  ProgressCheck,
} from "../types/Interfaces";

export namespace EvidenceHelpers {
  export function checkEvidencePartIsFilled(type: string, value: any): boolean {
    if (!value) {
      return false;
    }

    if (type === "SingleLine text" || type === "TextBox") {
      let string: string = value;

      return string.trim().length !== 0;
    }

    if (type === "Pull down list") {
      let string: string = value;

      return string.length !== 0;
    }

    if (type === "Date") {
      try {
        let date: Date = new Date(value);

        return isValid(date);
      } catch (error) {
        console.log(error);

        return false;
      }
    }

    if (type === "Tick box list" || type === "SingleTick") {
      let array: any[] = value;

      return array.length > 0;
    }

    if (type === "Number") {
      let number: string = value;

      return number.length > 0;
    }

    if (type === "RadioButton" || type === "Scale 1-5") {
      let string: string = value;

      return string.length !== 0;
    }

    if (type === "Radio yes/no with comments") {
      let radioValue: string = value.radioValue;
      // let textValue: string = value.textValue;

      return radioValue.length !== 0;
    }

    if (type === "Attachments") {
      let arrays = structuredClone(value);

      return (
        arrays?.attachmentsToAdd?.length > 0 || arrays?.attachmentsToDelete?.length !== arrays?.attachments?.length
      );
    }

    if (type === "MentorInfo") {
      return value !== undefined;
    }

    if (type === "MenteeInfo") {
      return value !== undefined;
    }

    return false;
  }

  export function checkLOsMultiSelectForNQPIsFilled(evidenceJSON: any): boolean {
    if (evidenceJSON.LearningObjectivesTickboxes?.selectedOutcomes) {
      return evidenceJSON.LearningObjectivesTickboxes.selectedOutcomes.length > 0;
    }

    return false;
  }

  export function generateUniqueID(): string {
    return (Date.now().toString(36) + Math.random().toString(36).substr(2, 5)).toUpperCase();
  }

  export function getEvidenceCountText(value: number): string {
    return `${value} evidence`;
  }

  export function getEvidenceParts(params: { program: ProgramData; evidence: IEvidence; user: any }): EvidencePart[] {
    const { program, evidence } = params;

    let evidenceJSON;

    if (typeof evidence.evidenceJSON === "string") {
      evidenceJSON = JSON.parse(evidence.evidenceJSON);
    } else if (typeof evidence.evidenceJSON === "object") {
      evidenceJSON = evidence.evidenceJSON;
    }

    const competenceName = evidenceJSON.programInfo?.competence;

    const competences = program.CompetenceActivity ?? [];
    const competence = competences.find((item: any): item is CompetenceActivityClass => item.Name === competenceName);

    const progressChecks = program.ProgressChecks ?? [];

    if (progressChecks.length > 0) {
      const progressCheckID = evidenceJSON.programInfo?.progressCheckID;
      const progressCheckCompetenceID = evidenceJSON.programInfo?.progressCheckCompetenceID;

      const progressCheck = progressChecks.find((item: any): item is ProgressCheck => item.ID === progressCheckID);

      const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;

      if (progressCheck) {
        const progressCheckCompetences = progressCheck?.["Competence/Activity"] ?? [];

        const progressCheckCompetence = progressCheckCompetences.find(
          (item: any): item is CompetenceActivityClass => item.ID === progressCheckCompetenceID
        );

        const evidenceDefinitions = progressCheckCompetence?.["Evidence Definitions"][0] ?? null;

        if (evidenceDefinitions) {
          return evidenceDefinitions["Evidence parts"] || evidenceDefinitions["Evidence parts Mandatory"] || [];
        }
      } else if (usesAdditionalEvidenceDefinitions) {
        const evidenceDefinitions = program["Evidence Definitions"] ?? [];

        if (evidenceDefinitions.length > 0) {
          const evidenceType = getEvidenceType({
            evidenceJSON,
            usesAdditionalEvidenceDefinitions,
            evidenceDefinitions,
          });

          let evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === evidenceType);

          const evidenceParts = evidenceDefinition?.["Evidence parts"] ?? [];
          return evidenceParts;
        }
      }
    } else if (competences.length > 0 && competence) {
      const evidenceDefinitions = competence["Evidence Definitions"][0] ?? null;

      if (evidenceDefinitions) {
        return evidenceDefinitions["Evidence parts"] ?? [];
      }
    } else if (program["Evidence Definitions"]) {
      const evidenceDefinitions = program["Evidence Definitions"];

      if (evidenceDefinitions && evidenceDefinitions.length === 1) {
        const evidenceDefinitions = program["Evidence Definitions"][0];
        return evidenceDefinitions["Evidence parts"] ?? [];
      } else if (evidenceDefinitions && evidenceDefinitions.length > 0) {
        const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;

        const evidenceType = getEvidenceType({
          evidenceJSON,
          usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions,
        });

        let evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === evidenceType);

        const evidenceParts = evidenceDefinition?.["Evidence parts"] ?? [];

        return evidenceParts;
      }

      return [];
    }

    return [];
  }

  export function titleHasNoData(evidenceParts: EvidencePart[]): boolean {
    const titlePart = evidenceParts.find((item) => item.Name === "Title");

    return titlePart == null;
  }

  export function getLearningObjectivesForStandard(
    mandatoryStandards: CompetenceActivityClass[] | undefined,
    optionalStandards: CompetenceActivityClass[] | undefined,
    selectedStandard: INOSSelectCompetence,
    learningObjectives: INOSSelectLearningObjectives[]
  ): INOSSelectLearningObjectives[] | undefined {
    const standards = mandatoryStandards?.concat(optionalStandards ?? []) ?? [];
    const standard = standards?.find((item) => item.ID === selectedStandard.id);
    const mappedLearningObjectives = standard?.["Learning Objective"]?.map((item) => item.ID) ?? [];

    const array = learningObjectives.filter((item) => mappedLearningObjectives.includes(item.id));

    return array ?? undefined;
  }

  function checkCompetenceActivityType<T extends object>(key: PropertyKey, object: T | string): key is keyof T {
    if (typeof object === "string") {
      return false;
    }

    return key in object;
  }

  export function getMandatoryStandards(user: any, program: ProgramData): CompetenceActivityClass[] {
    if (user && program) {
      const nwasICProgram = user.programRoles;
      const selectedRoleID = nwasICProgram?.programRoles[0].roleID;
      const role = program.Roles?.find((item: any) => item.ID === selectedRoleID);
      const activity = role?.CompetenceActivity?.[0];

      if (activity && checkCompetenceActivityType("Name", activity)) {
        const array: CompetenceActivityClass[] = role.CompetenceActivity as CompetenceActivityClass[];

        return array ?? [];
      }
    }

    return [];
  }

  export function getOptionalStandards(user: any, program: ProgramData): CompetenceActivityClass[] {
    if (user && program) {
      const nwasICProgram = user.programRoles;
      const selectedRoleID = nwasICProgram?.programRoles[0].roleID;

      const optionalStandards = nwasICProgram?.programRoles[0].optionalStandards ?? [];
      const optionalStandardsIDs = optionalStandards.map((item: any) => item.id);

      const role = program.Roles?.find((item) => item.ID === selectedRoleID);
      const activity = role?.OptionalCompetenceActivity?.[0];

      if (activity && checkCompetenceActivityType("Name", activity)) {
        const array: CompetenceActivityClass[] = role.OptionalCompetenceActivity as CompetenceActivityClass[];

        const selectedStandards = array.filter((item) => optionalStandardsIDs.includes(item.ID));

        return selectedStandards ?? [];
      }
    }

    return [];
  }

  export function isDefaultDate(partName: string, program: ProgramData | null): boolean {
    let parts: string[] = [];
    if (program?.ID === "LR0HYZYWY6JQK") {
      parts = ["Date of activity"];
    } else {
      parts = ["Date of reflection", "Date of Incident"];
    }

    return parts.includes(partName);
  }

  export function autofillMenteeInfo(part?: EvidencePart): boolean {
    if (!part) {
      return false;
    }

    return part.AutofillWithMenteeName ?? false;
  }

  export function getEvidenceType(params: {
    evidenceJSON: any;
    usesAdditionalEvidenceDefinitions: boolean;
    evidenceDefinitions: EvidenceDefinition[];
  }): string {
    const { evidenceJSON, usesAdditionalEvidenceDefinitions, evidenceDefinitions } = params;

    if (usesAdditionalEvidenceDefinitions) {
      const evidenceDefinition = evidenceDefinitions.find(
        (item) => item.ID === evidenceJSON.programInfo.programEvidenceDefinitionID
      );

      if (evidenceDefinition && evidenceDefinition.EvidenceSelectionType) {
        return evidenceDefinition.EvidenceSelectionType;
      }
    }

    return "standard";
  }
}
